import React, {
  useMemo,
  useId,
  useState,
  type ReactNode,
  useEffect,
  Fragment,
} from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents, type InputErrorType } from "@b2bportal/core-types";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  InputContainer,
  type InputContainerProps,
  InputError,
} from "@tenant/ui";
import clsx from "clsx";
import { Icon } from "../Icon";
import defaultStyles from "./Select.module.scss";

export interface SelectComponentProps extends SelectPrimitive.SelectProps {
  placeholder?: React.ReactNode;
  id?: string;
  label?: string;
  "aria-label"?: string;
  // Radix Content prop
  contentProps?: SelectPrimitive.SelectContentProps;
  className?: string;
  valueToDisplay?: string;
  dropdownIconName?: IconNameEnum;
  startIcon?: ReactNode;
  error?: InputErrorType;
  disablePortal?: boolean;
  inputContainerProps?: Partial<InputContainerProps>;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectComponentProps>(
  (
    {
      children,
      placeholder,
      valueToDisplay,
      contentProps,
      className,
      dropdownIconName,
      id,
      label,
      "aria-label": ariaLabel,
      startIcon,
      error,
      disablePortal = false,
      disabled = false,
      inputContainerProps,
      ...props
    },
    forwardedRef
  ) => {
    const COMPONENT_KEY = CoreUiComponents.Select;
    const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
    const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

    const reactId = useId();
    const internalId = useMemo(() => {
      return id ?? reactId;
    }, [id, reactId]);

    const [internalValue, setInternalValue] = useState(
      props.value ?? props.defaultValue
    );
    const hasValue = useMemo(() => {
      return internalValue != null && internalValue !== "";
    }, [internalValue]);

    useEffect(() => {
      setInternalValue(props.value);
    }, [props.value]);

    const Portal = disablePortal ? Fragment : SelectPrimitive.Portal;

    return (
      <SelectPrimitive.Root
        {...props}
        disabled={disabled}
        onValueChange={(e) => {
          setInternalValue(e);
          props.onValueChange?.(e);
        }}
      >
        <div className={cn("root")}>
          <SelectPrimitive.Trigger
            ref={forwardedRef}
            id={internalId}
            className={clsx(cn("trigger", { disabled }), className)}
            aria-label={label ?? ariaLabel}
          >
            <InputContainer
              {...inputContainerProps}
              className={cn("input-container")}
              id={internalId}
              label={label ?? inputContainerProps?.label}
              hasValue={hasValue}
              startContent={startIcon}
              hasError={error != null}
              endContent={
                <Icon
                  iconName={dropdownIconName ?? IconNameEnum.dropdownTriangle}
                  className={cn("trigger-icon")}
                />
              }
            >
              <div
                className={cn("trigger-label", {
                  "select-label": label != null,
                })}
              >
                <SelectPrimitive.Value
                  placeholder={<span>{placeholder}</span>}
                  aria-label={valueToDisplay}
                >
                  {valueToDisplay}
                </SelectPrimitive.Value>
              </div>
            </InputContainer>
          </SelectPrimitive.Trigger>

          <Portal>
            <SelectPrimitive.Content
              className={cn("content")}
              position="popper"
              sideOffset={5}
              arrowPadding={5}
              {...contentProps}
            >
              <SelectPrimitive.ScrollUpButton className={cn("scroll-button")}>
                <Icon
                  iconName={IconNameEnum.chevronUp}
                  className={cn("scroll-icon")}
                />
              </SelectPrimitive.ScrollUpButton>
              <SelectPrimitive.Viewport className={cn("view")}>
                {children}
              </SelectPrimitive.Viewport>
              <SelectPrimitive.ScrollDownButton className={cn("scroll-button")}>
                <Icon
                  iconName={IconNameEnum.chevronDown}
                  className={cn("scroll-icon")}
                />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </Portal>
          <InputError error={error} />
        </div>
      </SelectPrimitive.Root>
    );
  }
);

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  SelectPrimitive.SelectItemProps
>(({ children, className, ...props }, forwardedRef) => {
  const COMPONENT_KEY = CoreUiComponents.Select;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);

  return (
    <SelectPrimitive.Item
      {...props}
      ref={forwardedRef}
      className={clsx(cn("item"), className)}
    >
      <SelectPrimitive.ItemText>
        <span>{children}</span>
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator>
        <Icon
          iconName={IconNameEnum.check}
          className={cn("item-indicator-icon")}
        />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
